import React from 'react';

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button as MUIButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';

import scLogo from '../../angular-modules/build/img/sc-logo.png';

const useStyles = makeStyles()((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    color: '#e0e0e0',
    height: '180px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '600px',
    margin: '40px auto',
    padding: '32px',
    textAlign: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    color: theme.palette.custom?.HEADER_BAR_BG_COLOR,
  },
  verifyButton: {
    marginTop: theme.spacing(1),
    padding: '5px 32px',
    fontSize: '14px',
    backgroundColor: theme.palette.custom?.HEADER_BAR_BG_COLOR,
    '&:hover': {
      backgroundColor: theme.palette.custom?.HEADER_BAR_BG_COLOR,
      opacity: 0.9,
    },
  },
  logo: {
    width: '25%',
    marginBottom: theme.spacing(2),
  },
  token: { fontSize: '14px', wordBreak: 'break-all', textAlign: 'left', width: '100%' },
}));

type TOKENS = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

type Inputs = {
  client_id: string;
  client_secret: string;
};

function OhsAdaCodeExchange() {
  const [searchParams] = useSearchParams();
  const { classes } = useStyles();
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const code = searchParams.get('code');
  const [isLoading, setIsLoading] = React.useState(false);
  const [tokens, setTokens] = React.useState<TOKENS | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      client_id: clientId ? String(clientId) : '',
      client_secret: '',
    },
  });

  const formSubmit = async (data: Inputs) => {
    const params = {
      client_id: clientId ? String(clientId) : data.client_id || '',
      client_secret: String(data.client_secret) || '',
      redirect_uri: String(redirectUri) || '',
      code: String(code) || '',
      grant_type: 'authorization_code',
    };

    const formBody = new URLSearchParams(params).toString();
    setIsLoading(true);
    try {
      const res = await fetch('https://auth.ada.adadev.safetychampion.dev/oauth2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });
      const tokenResponse = await res.json();

      if (tokenResponse.error) {
        toast.error(
          'An error occurred while requesting tokens. Please check your details and try again.'
        );
        setTokens(null);
        return;
      }
      if (tokenResponse) {
        setTokens(tokenResponse);
        toast.success('Success! Your tokens have been generated.');
      }
    } catch (err) {
      toast.error(`Error during form submission: ${err}`);
      console.error('Error during form submission:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to copy text to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Helper to download text as a file
  const handleDownload = (filename: string, text: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Downloaded as text file!');
  };

  if (isLoading) {
    return (
      <Paper className={classes.container} sx={{ height: '400px' }}>
        <Box className={classes.loadingContainer}>
          <CircularProgress
            size={40}
            sx={{ color: (theme) => theme.palette.custom?.HEADER_BAR_BG_COLOR }}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <div>
      <Paper className={classes.container}>
        <form noValidate autoComplete="off" onSubmit={handleSubmit(formSubmit)}>
          <img className={classes.logo} src={scLogo} alt="Safety Champion" />
          <Typography variant="h5" gutterBottom className={classes.title}>
            API Direct Access - Code Exchange
          </Typography>
          <p>
            This static webpage is intended solely as a tool to help developers obtain refresh and
            access tokens. It is not meant for use in production environments. In a real-world
            scenario, you would need to implement your own <strong>redirectUri</strong> and handle
            the OAuth exchange protocol on your own.
          </p>
          <Box mt={4} mb={1} sx={{ width: '100%' }}>
            <TextField
              type="text"
              label="Client ID"
              id="client_id"
              fullWidth
              required
              {...register('client_id', { required: 'Client ID is required' })}
              error={!!errors.client_id}
              helperText={errors.client_id?.message}
            />
          </Box>
          <Box mt={2} mb={1} sx={{ width: '100%' }}>
            <TextField
              type="text"
              label="Client Secret Key"
              id="client_secret"
              fullWidth
              required
              {...register('client_secret', { required: 'Client secret is required' })}
              error={!!errors.client_secret}
              helperText={errors.client_secret?.message}
            />
          </Box>
          <MUIButton
            type="submit"
            color="primary"
            variant="contained"
            className={classes.verifyButton}
          >
            Request Tokens
          </MUIButton>
        </form>
        {tokens?.access_token && tokens?.refresh_token && (
          <Alert severity="info" sx={{ width: '100%', margin: '15px 0 10px 0', textAlign: 'left' }}>
            <Typography variant="body1" gutterBottom className={classes.title}>
              Tokens generated successfully. Remember: this tool is for development purposes only.
              Implement your own redirectUri and handle the OAuth exchange protocol in production
              environments.
            </Typography>
          </Alert>
        )}
        {tokens?.access_token && (
          <Box display="flex" alignItems="center" sx={{ width: '100%', mb: 1 }}>
            <Typography variant="body1" gutterBottom className={classes.token} sx={{ flex: 1 }}>
              <strong>Access Token:</strong> {tokens.access_token}
            </Typography>
            <IconButton
              aria-label="Copy access token"
              size="small"
              onClick={() => handleCopy(tokens.access_token)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Download access token"
              size="small"
              onClick={() => handleDownload('access_token.txt', tokens.access_token)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {tokens?.refresh_token && (
          <Box display="flex" alignItems="center" sx={{ width: '100%', mb: 1 }}>
            <Typography variant="body1" gutterBottom className={classes.token} sx={{ flex: 1 }}>
              <strong>Refresh Token:</strong> {tokens.refresh_token}
            </Typography>
            <IconButton
              aria-label="Copy refresh token"
              size="small"
              onClick={() => handleCopy(tokens.refresh_token)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Download refresh token"
              size="small"
              onClick={() => handleDownload('refresh_token.txt', tokens.refresh_token)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </div>
  );
}

export default OhsAdaCodeExchange;
